import {Component, forwardRef, Input} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MetroGroup} from '../../api/models/metro-group';
import {MetroGroupKey} from '../../api/models/metro-group-key';

@Component({
  selector: "metro-group-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetroGroupFilterEditorComponent),
      multi: true
    }
  ]
})
export class MetroGroupFilterEditorComponent extends FilterEditorComponent<MetroGroup, MetroGroupKey, TVSNMetroGroup, MetroGroupDataSource> implements ControlValueAccessor {

  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);
  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNMetroGroup, TVSNMetroGroup>, cacheDataService: CacheDataService): MetroGroupDataSource {
    return new MetroGroupDataSource(treeControl, cacheDataService);
  }
}


export class MetroGroupDataSource extends TreeViewDataSource<MetroGroup, MetroGroupKey, TVSNMetroGroup> {

  constructor(
    treeControl: FlatTreeControl<TVSNMetroGroup>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNMetroGroup[]) => void) {
    this.cacheDataService.cacheDataGetCleanLocationCountries({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.filter(item => item.countryIso === 'US').map(item => new TVSNMetroGroup(
        [], this.blankCover(item.countryIso), 1,
        {
          countryIso: item.countryIso
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNMetroGroup, callback: (results: TVSNMetroGroup[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetCleanLocationRegionsByCountry({ country: node.sourceValue.countryIso, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNMetroGroup[] = result.page.map(item => new TVSNMetroGroup(
          [], this.blankCover(item.stateProvinceRegion), node.level + 1,
          {
            countryIso: item.countryIso,
            stateProvinceRegion: item.stateProvinceRegion
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetMetroGroupsByCountryAndRegion({ country: node.sourceValue.countryIso, region: node.sourceValue.stateProvinceRegion, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNMetroGroup[] = result.page.filter(item => item.name !== '99999').map(item => new TVSNMetroGroup(
          [], this.blankCover(item.name), node.level + 1,
          {
            countryIso: item.countryIso,
            stateProvinceRegion: item.stateProvinceRegion,
            name: item.name
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }
}

export class TVSNMetroGroup extends TreeViewSelectionNode<MetroGroup, MetroGroupKey> {

  protected deriveKey(): MetroGroupKey {
    return {
      country: this.sourceValue.countryIso,
      stateProvinceRegion: this.sourceValue.stateProvinceRegion,
      metroGroup: this.sourceValue.name
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: MetroGroupKey[]): boolean {
    // Rules:
    // If city != null, there can be no children
    // If current level matches, it must NOT match entirely (i.e, there target should have a lower level than THIS node

    if (!this.sourceValue.name) {
      if (this.sourceValue.stateProvinceRegion) {
        return allKnownSelections?.some(x => x.stateProvinceRegion == this.sourceValue.stateProvinceRegion && x.metroGroup);
      } else if (this.sourceValue.countryIso) {
        return allKnownSelections?.some(x => x.country === this.sourceValue.countryIso && x.stateProvinceRegion);
      }
    }

    return false;
  }

  public isKeyForMe(key: MetroGroupKey): boolean {
    return key.metroGroup == this.sourceValue.name
    && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion
    && key.country === this.sourceValue.countryIso;
  }

  public isChildOf(key: MetroGroupKey): boolean {
    if (key.stateProvinceRegion != null) {
      return key.country === this.sourceValue.countryIso
        && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion;
    } else if (key.country != null) {
      return key.country === this.sourceValue.countryIso;
    }

    return false;
  }

  public toSourceKey(): MetroGroupKey {
    return {
      country: this.sourceValue.countryIso,
      stateProvinceRegion: this.sourceValue.stateProvinceRegion,
      metroGroup: this.sourceValue.name
    }
  }

  public matchesKey(key: MetroGroupKey): boolean {
    return key.metroGroup === this.sourceValue.name
    && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion
    && key.country === this.sourceValue.countryIso;
  }

}
